import { QUADRO_DA_FACE, RAIO_DO_PONTO, type MarcacaoFacial } from "@bookdental/schemas";

const W = QUADRO_DA_FACE.largura;
const H = QUADRO_DA_FACE.altura;

export const emPx = (x: number, y: number): [number, number] => [x * W, y * H];

export type EstiloDaMarcacao = "normal" | "apagada" | "destaque";

/**
 * Desenho de uma marcação no quadro (px): preenchimento translúcido na cor do procedimento e
 * contorno cheio. Sem estado nem eventos — serve ao editor e à impressão (renderToStaticMarkup).
 */
export function FormaDaMarcacao({
  m,
  cor,
  estilo = "normal",
  numero,
}: {
  m: MarcacaoFacial;
  cor: string;
  estilo?: EstiloDaMarcacao;
  /** Número da legenda (impressão). */
  numero?: number;
}) {
  const apagada = estilo === "apagada";
  const traco = apagada ? "#a1a1aa" : cor;
  const fundo = apagada ? "#a1a1aa" : cor;
  const opacidade = apagada ? 0.18 : estilo === "destaque" ? 0.55 : 0.35;
  const espessura = estilo === "destaque" ? 3 : 2;
  const [cx, cy] = m.tipo === "TRAJETO" ? emPx(...m.pontos[0]) : emPx(m.x, m.y);

  let forma;
  if (m.tipo === "TRAJETO") {
    const pts = m.pontos.map(([x, y]) => emPx(x, y).join(",")).join(" ");
    forma = (
      <>
        <polyline points={pts} fill="none" stroke={traco} strokeOpacity={apagada ? 0.5 : 0.9} strokeWidth={estilo === "destaque" ? 7 : 5} strokeLinecap="round" strokeLinejoin="round" />
        {m.pontos.map(([x, y], i) => {
          const [px, py] = emPx(x, y);
          return <circle key={i} cx={px} cy={py} r={3.5} fill={traco} />;
        })}
      </>
    );
  } else if (m.tipo === "ELIPSE") {
    forma = (
      <ellipse
        cx={cx}
        cy={cy}
        rx={m.rx * W}
        ry={m.ry * W}
        transform={`rotate(${m.rotacao} ${cx} ${cy})`}
        fill={fundo}
        fillOpacity={opacidade}
        stroke={traco}
        strokeWidth={espessura}
      />
    );
  } else {
    const raio = m.tipo === "AREA" ? m.raio * W : RAIO_DO_PONTO * W;
    forma = (
      <circle
        cx={cx}
        cy={cy}
        r={raio}
        fill={fundo}
        fillOpacity={m.tipo === "PONTO" ? (apagada ? 0.5 : 0.95) : opacidade}
        stroke={m.tipo === "PONTO" ? "#fff" : traco}
        strokeWidth={m.tipo === "PONTO" ? 1.5 : espessura}
      />
    );
  }
  return (
    <>
      {forma}
      {numero != null && (
        <g>
          <circle cx={cx + 14} cy={cy - 14} r={10} fill="#18181b" />
          <text x={cx + 14} y={cy - 10} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff" fontFamily="sans-serif">
            {numero}
          </text>
        </g>
      )}
    </>
  );
}

export const QUADRO_VIEWBOX = `0 0 ${W} ${H}`;
